// doc/09 Phase 1: Python sandbox monitor. Surfaces /api/admin/sandbox —
// the most recent `python_compute` runs and a 24h breakdown of failures
// and limiter rejections by structured error code. Read-only; a sandbox
// run is ephemeral, so there's no resolve workflow.
//
// Embedded as a tab inside AdminShell. Owns its own fetch/state; the
// shell handles tab nav + back-to-app.

import { useCallback, useEffect, useState } from 'react'
import { api, type SandboxExecRow } from '../api'

export function AdminSandbox() {
  const [rows, setRows] = useState<SandboxExecRow[] | null>(null)
  const [failures, setFailures] = useState<{ error: string; count: number }[]>([])
  const [windowHours, setWindowHours] = useState(24)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setError(null)
    try {
      const res = await api.listAdminSandbox()
      setRows(res.recent)
      setFailures(res.failures_24h)
      setWindowHours(res.window_hours)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const totalFailures = failures.reduce((n, f) => n + f.count, 0)

  return (
    <div style={{ fontSize: 13, color: 'var(--ink-1)' }}>
      {/* 24h failure / limiter-rejection breakdown */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>last {windowHours}h:</span>
        {totalFailures === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            no failures or rejections
          </span>
        ) : (
          failures.map((f) => (
            <span
              key={f.error}
              style={{
                background: 'var(--paper-2)',
                border: '1px solid var(--rule-s)',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 11,
                fontFamily: 'monospace',
              }}
            >
              {f.error} ×{f.count}
            </span>
          ))
        )}
        <button
          type="button"
          onClick={() => void reload()}
          style={{
            background: 'transparent',
            border: '1px solid var(--rule-s)',
            color: 'var(--ink-2)',
            padding: '3px 10px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 12,
            marginLeft: 'auto',
          }}
        >
          refresh
        </button>
      </div>

      {error && (
        <div
          style={{
            background: 'var(--red-soft)',
            border: '1px solid var(--red)',
            padding: '6px 10px',
            borderRadius: 4,
            color: 'var(--red)',
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {rows === null ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>
          no sandbox runs recorded yet
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--rule-s)', textAlign: 'left' }}>
              <th style={th}>created_at</th>
              <th style={th}>outcome</th>
              <th style={th}>exit</th>
              <th style={th}>duration</th>
              <th style={th}>tenant</th>
              <th style={th}>machine</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <SandboxRowView key={r.id} row={r} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

const th: React.CSSProperties = {
  padding: '6px 8px',
  fontWeight: 500,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
}

const td: React.CSSProperties = {
  padding: '6px 8px',
  verticalAlign: 'top',
  borderBottom: '1px solid var(--rule-xs, #eee)',
}

const preStyle: React.CSSProperties = {
  margin: '4px 0 0',
  padding: 8,
  background: 'var(--paper-0, white)',
  border: '1px solid var(--rule-s)',
  borderRadius: 4,
  fontSize: 11,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 280,
  overflow: 'auto',
}

const summaryStyle: React.CSSProperties = {
  cursor: 'pointer',
  fontSize: 11,
  color: 'var(--ink-3)',
}

// A clean exit_code 0 is "ok"; a structured `error` is a failure /
// rejection (red); exit 1/2 is a Python exception the agent reasons on
// (amber — not a sandbox fault).
function outcome(row: SandboxExecRow): { label: string; color: string } {
  if (row.error) return { label: row.error, color: 'var(--red)' }
  if (row.exit_code === 0) return { label: 'ok', color: 'var(--green, #2a7d4f)' }
  return {
    label: `exception (exit ${row.exit_code ?? '—'})`,
    color: 'var(--amber, #b8860b)',
  }
}

function SandboxRowView({ row }: { row: SandboxExecRow }) {
  const [expanded, setExpanded] = useState(false)
  const created = new Date(row.created_at)
  const createdStr = `${created.toLocaleDateString()} ${created.toLocaleTimeString()}`
  const oc = outcome(row)
  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
        <td style={td}>{createdStr}</td>
        <td style={{ ...td, color: oc.color, fontWeight: 500 }}>{oc.label}</td>
        <td style={{ ...td, fontFamily: 'monospace' }}>{row.exit_code ?? '—'}</td>
        <td style={td}>{row.duration_ms != null ? `${row.duration_ms} ms` : '—'}</td>
        <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--ink-3)' }}>
          {row.tenant_id.slice(0, 8)}
        </td>
        <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--ink-3)' }}>
          {row.machine_id ?? '—'}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td
            colSpan={6}
            style={{ padding: '8px 12px', background: 'var(--paper-1, #fafafa)' }}
          >
            <details open style={{ marginBottom: 6 }}>
              <summary style={summaryStyle}>code</summary>
              <pre style={preStyle}>{row.code}</pre>
            </details>
            {row.stdout && (
              <details style={{ marginBottom: 6 }}>
                <summary style={summaryStyle}>stdout</summary>
                <pre style={preStyle}>{row.stdout}</pre>
              </details>
            )}
            {row.stderr && (
              <details>
                <summary style={summaryStyle}>stderr</summary>
                <pre style={preStyle}>{row.stderr}</pre>
              </details>
            )}
          </td>
        </tr>
      )}
    </>
  )
}
